import { isNullOrUndefined } from "../../../../../../../tools";
import { IdProvider } from "../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../services/utils.service";
import { DataContainer } from "../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../infrastructure/validationresultaccumulator";
import { ValidationMessages, ValidationPatterns } from "../../../../constants";
import { PHCFetchRequest } from "./phcfetchrequest";

export class PHCProps {

 public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public state_id: number = 0;
  public readonly state_name_en: string = '';
  public readonly state_name_mr: string = '';
  public zone_id: number = 0;
  public readonly zone_name: string = '';
  public district_id: number = 0;
  public readonly district_name_en: string = '';
  public readonly district_name_mr: string = '';
  public block_id: number = 0;
  public readonly block_name_en: string = '';
  public readonly block_name_mr: string = '';
  public bcm_post_id: number = 0;
  public readonly bcm_post_name: string = '';
  public name: string = '';
  public name_mr: string = '';
  public month: string = '';
  public sanctioned_no_of_af_in_phc: number = 0;
  public sanctioned_no_of_asha_in_phc: number = 0;
  
  public financial_year_id: number = 0;

  public readonly is_newly_created: boolean = false;

  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new PHCProps(true);
  }
}

export class PHC implements IPersistable<PHC> {
  public static readonly mastertablename: string = 'phc_master';

  private constructor(public readonly p: PHCProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): PHC {
    let newPHC: PHCProps = Utils.GetInstance().DeepCopy(this.p);
    return PHC.CreateInstance(newPHC, true);
  }

  public static CreateNewInstance() {
    return new PHC(PHCProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new PHC(data as PHCProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');
    if (!this.p.name || this.p.name.trim() == '') { vra.add('name', 'PHC Name cannot be blank. ');
    }
    if (this.p.block_id  <= 0) vra.add('block_id', 'Block Name cannot be Blank.');
    if (this.p.bcm_post_id  <= 0) vra.add('bcm_post_id', 'BCM Name cannot be Blank.');

    
       // Validate Month (string but numeric)
    const monthNum = Number(this.p.month);
    if (!this.p.month || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    vra.add('month', 'Please select a valid month.');
    }
     // ✔ Correct validation for compulsory + > 0
  if (!this.p.sanctioned_no_of_af_in_phc ||this.p.sanctioned_no_of_af_in_phc <= 0) {
    vra.add('sanctioned_no_of_af_in_phc', 'Sanctioned No. of AF must be greater than 0.');
  }
  if (!this.p.sanctioned_no_of_asha_in_phc ||this.p.sanctioned_no_of_asha_in_phc <= 0) {
    vra.add('sanctioned_no_of_asha_in_phc', 'Sanctioned No. of ASHA must be greater than 0.');
  }
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, PHC.mastertablename, this.p);
  }

  private static m_currentInstance: PHC = PHC.CreateNewInstance();

  public static GetCurrentInstance() {
    return PHC.m_currentInstance;
  }

  public static SetCurrentInstance(value: PHC) {
    PHC.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): PHC {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, PHC.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, PHC.mastertablename)!.Entries) {
        return PHC.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): PHC[] {
    let result: PHC[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, PHC.mastertablename)) {
      let coll = dcs.GetCollection(cont, PHC.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(PHC.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): PHC[] {
    return PHC.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: PHCFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new PHCFetchRequest();
    req.phc_ids.push(id);

    let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
    return PHC.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: PHCFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
    return PHC.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new PHCFetchRequest();
    let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
    return PHC.ListFromTransportData(tdResponse);
  }

    // get all asha for report filter
      public static async FetchEntireListForReportFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
        let req = new PHCFetchRequest();
        let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
        return PHC.ListFromTransportData(tdResponse);
      }

  public static async FetchEntireListByBlockRef(block_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new PHCFetchRequest();
    req.block_ids.push(block_id)
    let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
    return PHC.ListFromTransportData(tdResponse);
  }

    public static async FetchEntireListByBCMPostRef(bcm_post_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new PHCFetchRequest();
    req.bcm_post_ids.push(bcm_post_id)
    let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
    return PHC.ListFromTransportData(tdResponse);
  }

   public static async FetchEntireListByBlockRefs(block_id: number [], errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new PHCFetchRequest();
    req.block_ids = block_id
    let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
    return PHC.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByZoneDistrictBlockRef(zone_id: number, district_id: number, block_id: number,bcm_post_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new PHCFetchRequest();
     if (zone_id) {
      req.zone_ids.push(zone_id);
    }
     if (district_id) {
      req.district_ids.push(district_id);
    }
    if (block_id) {
      req.block_ids.push(block_id);
    }
    if (bcm_post_id) {
      req.bcm_post_ids.push(bcm_post_id);
    }
    let tdResponse = await PHC.FetchTransportData(req, errorHandler) as TransportData;
    return PHC.ListFromTransportData(tdResponse);
  }

  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
